import moment from 'moment'

type SortFunction<Data> = (a: Data, b: Data) => number

export function compositeSorts<Data>(
  ...sorts: [SortFunction<Data>, ...SortFunction<Data>[]]
): SortFunction<Data> {
  return (a: Data, b: Data) => {
    for (const sort of sorts) {
      const sortPosition = sort(a, b)
      if (sortPosition !== 0) return sortPosition
    }
    return 0
  }
}

export const textSort: SortFunction<string> = (a: string, b: string) => {
  return a.trim().localeCompare(b.trim())
}

export function timeSort<Data>({
  format,
  granularity,
  getSortProperty,
  order = 'ASC'
}: {
  format?: string
  granularity?: moment.unitOfTime.StartOf
  getSortProperty: (data: Data) => moment.MomentInput
  order?: 'ASC' | 'DESC'
}): SortFunction<Data> {
  return (a: Data, b: Data): number => {
    const isAAfterB = moment(getSortProperty(a), format).isAfter(
      moment(getSortProperty(b), format),
      granularity
    )

    const isABeforeB = moment(getSortProperty(a), format).isBefore(
      moment(getSortProperty(b), format),
      granularity
    )

    if (order === 'ASC') {
      return isAAfterB ? 1 : isABeforeB ? -1 : 0
    } else {
      return isAAfterB ? -1 : isABeforeB ? 1 : 0
    }
  }
}

export function createEnumValueGetter<Enum>(enumAsArray: readonly Enum[]) {
  return (value: Enum): number => {
    return enumAsArray.findIndex((item) => value === item)
  }
}
export function enumSort<Data, Enum>({
  enumData,
  getSortProperty
}: {
  enumData: Enum[]
  getSortProperty: (data: Data) => Enum
}): SortFunction<Data> {
  const getEnumValue = createEnumValueGetter(enumData)

  return (a: Data, b: Data): number =>
    getEnumValue(getSortProperty(a)) > getEnumValue(getSortProperty(b))
      ? 1
      : getEnumValue(getSortProperty(a)) < getEnumValue(getSortProperty(b))
        ? -1
        : 0
}
